// ---------------------------------------------------------------------------
// message-store — single source of truth for the chat message list
//
// The chat UI integrates messages from many independent sources:
//
//   • initial history load            (GET ?limit=N)
//   • "load older messages"           (GET ?before=<id>)
//   • notification refetch            (GET ?after=<id>)
//   • post-stream reconciliation      (GET ?after=<id>)
//   • SSE streaming                   (optimistic user + assistant placeholder)
//   • run polling / resume            (GET ?after=<id>)
//
// Previously each path mutated the array with its own positional logic
// (prepend, `replaceAfterCursor`, ad-hoc sort) and tracked cursors in
// separate refs/state. With no id-level deduplication, any overlap between
// pages — or a spuriously re-fired effect — produced duplicate messages in
// the rendered list (e.g. the latest exchange rendered twice after loading
// older history).
//
// This module replaces all of that with ONE idempotent merge primitive,
// `mergeMessages`, plus deterministic ordering and store-derived cursors.
// Every update path funnels through `mergeMessages`, so re-fetching a page or
// re-running an effect can never duplicate a message: rows are upserted by id.
// ---------------------------------------------------------------------------

import type { AgnoChatMessage } from "@/hooks/use-agno-chat";

/**
 * A message as stored in the list. `localSeq` is a monotonic insertion
 * sequence assigned the first time a message enters the store; it is used only
 * to keep multiple optimistic (no-timestamp) messages in insertion order.
 */
export interface StoredMessage extends AgnoChatMessage {
  localSeq?: number;
}

/**
 * Parse a message's canonical creation time into epoch millis, or `null` when
 * the message has no parseable timestamp.
 *
 * A message is considered "canonical" (persisted in the DB) when it carries a
 * `metadata.createdAt`. Optimistic SSE messages (the just-sent user message
 * and the streaming assistant placeholder) have no timestamp and return null.
 */
function timestampMs(m: StoredMessage): number | null {
  const t = m.metadata?.createdAt;
  if (!t) return null;
  const ms = Date.parse(String(t));
  return Number.isFinite(ms) ? ms : null;
}

/** Preserve database precision beyond JavaScript Date's millisecond limit. */
function subMillisecondDigits(m: StoredMessage): number {
  const value = String(m.metadata?.createdAt ?? "");
  const match = value.match(/\.([0-9]+)(?:Z|[+-][0-9]{2}:[0-9]{2})$/);
  if (!match) return 0;
  return Number(match[1].slice(3, 9).padEnd(6, "0"));
}

/** True for canonical (timestamped, DB-backed) messages. */
export function isCanonical(m: StoredMessage): boolean {
  return timestampMs(m) !== null;
}

/**
 * Deterministic ordering for the rendered list:
 *
 *  1. Canonical messages first, chronological by `createdAt`, with the message
 *     id as a stable tiebreak when two share an exact timestamp.
 *  2. Optimistic (no-timestamp) messages pinned to the tail, in insertion
 *     order (`localSeq`). This keeps the just-sent message and the streaming
 *     assistant placeholder at the bottom of the conversation.
 */
export function compareMessages(a: StoredMessage, b: StoredMessage): number {
  const aMs = timestampMs(a);
  const bMs = timestampMs(b);

  if (aMs !== null && bMs !== null) {
    if (aMs !== bMs) return aMs - bMs;
    const preciseDifference = subMillisecondDigits(a) - subMillisecondDigits(b);
    if (preciseDifference !== 0) return preciseDifference;
    return a.id.localeCompare(b.id);
  }
  // Canonical (timestamped) always sorts before optimistic (no timestamp).
  if (aMs !== null) return -1;
  if (bMs !== null) return 1;
  // Both optimistic → preserve insertion order.
  return (a.localSeq ?? 0) - (b.localSeq ?? 0);
}

/**
 * Compare the file parts (used for attachment chips/thumbnails) of two
 * messages for render equality. Order matters.
 */
function fileParts(m: StoredMessage): Array<Record<string, unknown>> {
  return (m.parts ?? []).filter((p: any) => p.type === "file") as any[];
}

/**
 * Concatenated text of all `text` parts — mirrors how `buildRenderItems`
 * derives the bubble text.
 */
function textOfParts(m: StoredMessage): string {
  return (m.parts ?? [])
    .filter((p: any) => p.type === "text")
    .map((p: any) => p.text)
    .join("");
}

/**
 * True when two messages are indistinguishable for everything that affects
 * rendering, ordering, cursor derivation, polling status, and resume behavior.
 *
 * Used by {@link mergeMessages} to preserve object identity (and the whole
 * array reference) when a merge is a content no-op — e.g. re-fetching the same
 * page or re-polling an unchanged in-progress row — so React skips the
 * re-render. Returning `true` here when something actually changed would freeze
 * the UI, so the field set is deliberately exhaustive. See the field rationale
 * in the merge docstring.
 */
function messageRenderRelevantEqual(a: StoredMessage, b: StoredMessage): boolean {
  if (
    a.id !== b.id ||
    a.role !== b.role ||
    (a.content ?? "") !== (b.content ?? "") ||
    a.toolGroupBoundaryId !== b.toolGroupBoundaryId ||
    !!a.streamingError !== !!b.streamingError ||
    a.resumeTimestamp !== b.resumeTimestamp
  ) {
    return false;
  }
  if (textOfParts(a) !== textOfParts(b)) return false;

  const am = a.metadata ?? {};
  const bm = b.metadata ?? {};
  if (
    am.createdAt !== bm.createdAt ||
    am.status !== bm.status ||
    am.runId !== bm.runId ||
    am.toolName !== bm.toolName ||
    am.workflowTaskId !== bm.workflowTaskId ||
    am.sessionId !== bm.sessionId ||
    am.navigationTurnId !== bm.navigationTurnId ||
    am.navigationPartialContent !== bm.navigationPartialContent ||
    am.navigationContentCursor !== bm.navigationContentCursor ||
    am.navigationContentRevision !== bm.navigationContentRevision ||
    am.toolDetailSource !== bm.toolDetailSource ||
    am.voiceTranscript !== bm.voiceTranscript ||
    // HITL fields — when a run pauses for approval, the marker row carries
    // hitlPaused=true and hitlResolved=false.  When the user approves or
    // rejects (possibly on another device), the backend UPDATEs the row's
    // metadata to set resolved=true and writes the confirmed decision back
    // into the requirements' tool_execution.confirmed.  Without tracking
    // these fields here, a re-fetched marker would be treated as unchanged
    // and the UI would keep showing an interactive approval prompt instead
    // of the resolved "✓ Approved" / "✕ Rejected" state.
    am.hitlResolved !== bm.hitlResolved ||
    am.hitlRecoveryError !== bm.hitlRecoveryError ||
    am.hitlPaused !== bm.hitlPaused
  ) {
    return false;
  }
  // Live tool values are JSON-compatible stream data. Compare their compact
  // representations so a terminal result/error redraws the existing local
  // badge without turning historical rows into content-bearing messages.
  if (JSON.stringify(am.liveToolDetails) !== JSON.stringify(bm.liveToolDetails)) {
    return false;
  }

  const af = fileParts(a);
  const bf = fileParts(b);
  if (af.length !== bf.length) return false;
  for (let i = 0; i < af.length; i++) {
    if (
      af[i].filename !== bf[i].filename ||
      af[i].mediaType !== bf[i].mediaType ||
      af[i].url !== bf[i].url
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Idempotently merge `incoming` messages into `current`.
 *
 * - Messages are keyed by `id`; an incoming message with a known id UPDATES the
 *   existing entry (preserving its `localSeq`) rather than appending a copy.
 * - New messages are assigned the next `localSeq`.
 * - The result is always sorted by {@link compareMessages}.
 * - When the merge would not change anything render-relevant (every incoming
 *   row already exists and is {@link messageRenderRelevantEqual} to its stored
 *   copy, and no optimistic row is dropped), the EXACT SAME `current` array
 *   reference is returned. This lets repeated identical polls/refetches skip
 *   the `setMessages` re-render, and lets per-message render caches key off
 *   stable object identity.
 *
 * @param opts.dropOptimistic When true, drop all optimistic (no-timestamp)
 *   messages before applying `incoming`. Used by post-stream reconciliation to
 *   swap the temporary SSE placeholders for their canonical DB rows. Only pass
 *   this when `incoming` actually contains the canonical replacements,
 *   otherwise the optimistic messages would vanish with nothing to replace
 *   them.
 */
export function mergeMessages(
  current: StoredMessage[],
  incoming: StoredMessage[],
  opts?: { dropOptimistic?: boolean },
): StoredMessage[] {
  const byId = new Map<string, StoredMessage>();
  let maxSeq = 0;
  let changed = false;
  let newestCanonicalTimestamp: number | null = null;
  const optimisticUserBoundaryIds: string[] = [];

  for (const m of current) {
    byId.set(m.id, m);
    if (m.localSeq != null && m.localSeq > maxSeq) maxSeq = m.localSeq;
    const timestamp = timestampMs(m);
    if (timestamp !== null) {
      newestCanonicalTimestamp = newestCanonicalTimestamp === null
        ? timestamp
        : Math.max(newestCanonicalTimestamp, timestamp);
    } else if (m.role === "user") {
      // Capture this even before dropOptimistic. An initial-history request
      // can race the live stream and introduce the canonical user first;
      // giving it the stable boundary now makes a later drop idempotent.
      optimisticUserBoundaryIds.push(m.toolGroupBoundaryId ?? m.id);
    }
  }

  // When dropping optimistic messages, collect local file parts representing
  // failed uploads so they remain downloadable from the canonical user row.
  let orphanedFileParts: Array<Record<string, unknown>> = [];
  if (opts?.dropOptimistic) {
    for (const [id, m] of [...byId]) {
      if (!isCanonical(m)) {
        // Collect failed-upload file parts before dropping the optimistic row.
        if (m.role === "user") {
          const parts = fileParts(m);
          if (parts.length > 0) {
            orphanedFileParts = orphanedFileParts.concat(parts);
          }
        }
        byId.delete(id);
        changed = true;
      }
    }
  }

  // Reconciliation responses can contain older rows alongside the just-
  // persisted turn. Match optimistic boundaries only to newly-seen canonical
  // users at or beyond the previous tail, in chronological order, instead of
  // assigning the live group's identity to a paginated historical user.
  const boundaryIdByCanonicalUserId = new Map<string, string>();
  if (optimisticUserBoundaryIds.length > 0) {
    const replacementUsers = incoming
      .filter((m) => {
        if (m.role !== "user" || !isCanonical(m) || byId.has(m.id)) {
          return false;
        }
        const timestamp = timestampMs(m)!;
        return newestCanonicalTimestamp === null
          || timestamp >= newestCanonicalTimestamp;
      })
      .slice()
      .sort(compareMessages);
    const transferCount = Math.min(
      optimisticUserBoundaryIds.length,
      replacementUsers.length,
    );
    const boundaryOffset = optimisticUserBoundaryIds.length - transferCount;
    const replacementOffset = replacementUsers.length - transferCount;
    for (let index = 0; index < transferCount; index += 1) {
      boundaryIdByCanonicalUserId.set(
        replacementUsers[replacementOffset + index].id,
        optimisticUserBoundaryIds[boundaryOffset + index],
      );
    }
  }

  for (const m of incoming) {
    const existing = byId.get(m.id);
    if (existing) {
      // Upsert: take incoming fields but keep the original insertion sequence.
      const merged = { ...existing, ...m, localSeq: existing.localSeq };
      if (messageRenderRelevantEqual(existing, merged)) {
        // No render-relevant change — keep the existing object identity so the
        // result can stay reference-equal to `current`.
        byId.set(m.id, existing);
      } else {
        byId.set(m.id, merged);
        changed = true;
      }
    } else {
      // Carry over orphaned file parts from dropped optimistic messages.
      // Attach them to the first canonical user message that has no file
      // parts of its own.  This preserves locally-attached files (e.g.
      // voice notes whose server upload failed) through reconciliation.
      let partsToUse = m.parts;
      if (
        orphanedFileParts.length > 0 &&
        m.role === "user" &&
        fileParts(m).length === 0
      ) {
        partsToUse = [
          ...orphanedFileParts.map((p) => ({ ...p, type: "file" as const })),
          ...(m.parts ?? []),
        ];
        orphanedFileParts = [];
      }
      const toolGroupBoundaryId = boundaryIdByCanonicalUserId.get(m.id);
      byId.set(m.id, {
        ...m,
        parts: partsToUse,
        localSeq: m.localSeq ?? ++maxSeq,
        ...(toolGroupBoundaryId ? { toolGroupBoundaryId } : {}),
      });
      changed = true;
    }
  }

  // Nothing changed: return the same array reference so consumers can bail out.
  if (!changed) return current;

  return [...byId.values()].sort(compareMessages);
}

/**
 * The id of the oldest canonical message — used as the `before` cursor for
 * backward (older) pagination. Returns null when no canonical message exists
 * (e.g. a brand-new session whose only messages are optimistic).
 */
export function selectOldestCanonicalId(messages: StoredMessage[]): string | null {
  for (const m of messages) {
    if (isCanonical(m)) return m.id;
  }
  return null;
}

/**
 * The id of the newest canonical message — used as the `after` cursor for
 * forward (newer) pagination, notification refetch, and post-stream
 * reconciliation. Optimistic messages are skipped (they have no DB id yet).
 */
export function selectNewestCanonicalId(messages: StoredMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isCanonical(messages[i])) return messages[i].id;
  }
  return null;
}

/**
 * The id of the second-newest canonical message — used as the `after` cursor
 * for the visibility-refresh fetch.  By going one message back, the fetch
 * re-includes the newest message itself, so an in-progress assistant row
 * (partially written when the user switched tabs away) gets its final content
 * re-fetched and updated on return.  `mergeMessages` upserts by id, so
 * re-fetching an already-complete message is a harmless no-op.
 *
 * Returns null when fewer than two canonical messages exist (the normal
 * `?after=<newest>` path is fine for the first turn — there's nothing before
 * it to re-fetch, and an empty cursor means fetch from the beginning).
 */
export function selectSecondNewestCanonicalId(
  messages: StoredMessage[],
): string | null {
  let found = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isCanonical(messages[i])) {
      found++;
      if (found === 2) return messages[i].id;
    }
  }
  return null;
}
